<template>
  <!-- Add this banner before the main section -->
  <div class="container mt-4 px-3">
    <div class="automation-banner d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center gap-3">
        <div class="automation-icon">
          <i class="fas fa-sync-alt fa-spin-hover"></i>
        </div>
        <div>
          <h6 class="mb-1">Sincronización con GSG activa</h6>
          <p class="text-muted mb-0 small">Los precios se actualizan automáticamente con Grow Sweet Grow</p>
        </div>
      </div>
      <div class="automation-status">
        <span class="status-dot"></span>
        <span class="status-text">Conectado</span>
      </div>
    </div>
  </div>

  <section class="container-fluid">
    <div class="container mt-5 px-3">
      <!-- Header Section -->
      <div class="row align-items-center mb-5">
        <div class="col-md-6 mb-3 mb-md-0">
          <div class="d-flex align-items-center gap-3">
            <h2 class="mb-1 d-flex align-items-center text-light fs-1">
              Inventario<span class="text-primary">.</span>
            </h2>
          </div>
          <p class="text-muted mb-0 fs-5">
            <i class="fas fa-box-open me-1 text-primary"></i>
            Buscá y administra tus productos en el catálogo.
          </p>
          <!-- Filters Section -->
        <div class="col-md-10 my-3 mb-md-0">
          <div class="d-flex gap-3">
            <div class="input-group">
              <span class="input-group-text bg-white border-end-0 text-muted">
                <i class="fas fa-search"></i>
              </span>
              <input
                type="text"
                class="form-control border-start-0 ps-0 text-black"
                v-model="searchQuery"
                placeholder="Buscar por nombre o marca..."
                @input="debounceSearch"
              />
            </div>
          </div>
      </div>
        </div>
        <div class="col-md-6 d-lg-flex justify-content-lg-end">
          <div class="d-flex gap-3">
            <button 
              class="btn btn-outline-warning"
              @click="showBulkPriceUpdate"
            >
              <i class="fas fa-tags me-2"></i>
              Actualizar precios
            </button>
            <button 
              class="btn btn-outline-light d-flex align-items-center gap-2 text-white fw-semibold" 
              :class="{ 'active': showNoPrices }"
              @click="toggleNoPrices"
            >
              <i class="fas" :class="showNoPrices ? 'fa-times text-danger' : 'fa-tags'"></i>
              <span>Productos sin precio</span>
              <span 
                v-if="productsWithoutPrices > 0" 
                class="badge bg-danger d-none"
                style="font-size: 0.8em;"
              >
                {{ productsWithoutPrices }}
              </span>
            </button>
            <a href="/admin/productos/nuevo/" class="btn btn-primary">
              <i class="fas fa-plus me-2"></i>
              Agregar producto
            </a>
          </div>
        </div>
      </div>


      <!-- Loading State -->
      <div v-if="isLoading" class="row g-4">
        <div v-for="n in 8" :key="n" class="col-lg-4">
          <div class="skeleton-loader">
            <div class="skeleton-text w-50"></div>
            <div class="skeleton-text w-75"></div>
            <div class="skeleton-text w-75"></div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
        <button @click="fetchProducts" class="btn btn-outline-danger btn-sm ms-3">
          <i class="fas fa-sync-alt me-1"></i>Reintentar
        </button>
      </div>

      <!-- Products List -->
      <div v-else class="row g-4">
        <div v-for="product in filteredProducts" :key="product.id" class="col-md-6 col-lg-4">
          <div class="card bg-dark text-light">
            <div class="card-body d-flex flex-column">
              <!-- Product header -->
              <div class="d-flex align-items-center mb-3">
                <img
                  :src="product.image || require('@/assets/images/zapiola-logo.png')"
                  :alt="product.name"
                  class="img-thumbnail me-3"
                  style="width: 100px; height: auto; object-fit: cover;"
                />
                <div>
                  <h5 class="mb-1 fs-6 fw-bold">{{ product.name }}</h5>
                  <p class="mb-1 text-muted">{{ truncateText(product.description, 40) }}</p>
                  <small class="text-muted">{{ product.brand }}</small>
                </div>
              </div>
              <!-- Product details -->
              <div class="d-flex justify-content-between align-items-end flex-grow-1">
                
                <div class="d-flex flex-column align-items-end gap-1 w-50">
                  <!-- Mostrar rango de precios -->
                  <div class="price mb-2 text-success" v-if="product.blisters && product.blisters.length > 0">
                    <span v-if="getMinPrice(product) === getMaxPrice(product)">
                      ${{ formatPrice(getMinPrice(product)) }}
                    </span>
                    <span v-else>
                      ${{ formatPrice(getMinPrice(product)) }} - ${{ formatPrice(getMaxPrice(product)) }}
                    </span>
                  </div>
                  <div class="d-flex gap-2 w-100">
                    <button 
                      class="btn btn-danger col-4" 
                      @click="confirmDelete(product)"
                      title="Eliminar producto"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                    <router-link 
                      class="btn btn-secondary col" 
                      :to="{ name: 'ProductEdit', params: { productId: product.id }}"
                    >
                      <i class="fas fa-edit me-1"></i>
                      Editar
                    </router-link>
                  </div>
                  <router-link 
                    class="btn btn-primary w-100" 
                    :to="{ name: 'ProductDetails', params: { id: product.id }}"
                  >
                    <i class="fas fa-eye me-1"></i>
                    Publicación
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="d-flex justify-content-center mt-4">
        <nav aria-label="Page navigation">
          <ul class="pagination">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="loadProducts(currentPage - 1)" :disabled="currentPage === 1">
                <i class="fas fa-chevron-left"></i>
              </button>
            </li>
            <li v-for="page in totalPages" :key="page" class="page-item" :class="{ active: page === currentPage }">
              <button class="page-link" @click="loadProducts(page)">{{ page }}</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button class="page-link" @click="loadProducts(currentPage + 1)" :disabled="currentPage === totalPages">
                <i class="fas fa-chevron-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <!-- Empty State -->
      <div v-if="!isLoading && products.length === 0" class="text-center py-5 text-muted">
        <i class="fas fa-seedling fa-3x mb-3"></i>
        <h4>No se encontraron productos</h4>
        <p>Prueba con otros filtros de búsqueda</p>
      </div>
    </div>
  </section>

  <!-- Delete Confirmation Modal -->
  <div class="modal fade" id="deleteProductModal" tabindex="-1" aria-labelledby="deleteProductModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content bg-dark">
        <div class="modal-header border-bottom border-secondary">
          <h5 class="modal-title text-light" id="deleteProductModalLabel">Confirmar eliminación</h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p class="text-light mb-1">¿Estás seguro de que deseas eliminar el siguiente producto?</p>
          <div v-if="productToDelete" class="mt-3 p-3 border border-secondary rounded">
            <h6 class="text-light">{{ productToDelete.name }}</h6>
            <p class="text-muted small mb-0">{{ truncateText(productToDelete.description, 100) }}</p>
          </div>
        </div>
        <div class="modal-footer border-top border-secondary">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
          <button 
            type="button" 
            class="btn btn-danger" 
            @click="handleDelete" 
            :disabled="isDeleting"
          >
            <span v-if="isDeleting" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ isDeleting ? 'Eliminando...' : 'Eliminar' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add modal component -->
  <BulkPriceUpdateModal 
    ref="bulkPriceUpdateModal"
    @update-complete="loadProducts(currentPage)"
  />

</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue';
import { getProducts, deleteProduct } from '@/services/products';
import { useToast } from 'vue-toastification';
import { Modal } from 'bootstrap';
import BulkPriceUpdateModal from '@/components/BulkPriceUpdateModal.vue';

const toast = useToast();

// Estado
const products = ref([]);
const isLoading = ref(true);
const error = ref(null);
const searchQuery = ref('');
const currentPage = ref(1);
const pageSize = ref(12); // Ajustar según necesidad 
const totalItems = ref(0);
const showNoPrices = ref(false); // Add new ref for filter
const productToDelete = ref(null);
const isDeleting = ref(false);
let deleteModal = null;
const bulkPriceUpdateModal = ref(null);

// Computeds
const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value));

const loadProducts = async (page = 1) => {
  try {
    isLoading.value = true;
    error.value = null;
    
    const params = {
      page,
      page_size: pageSize.value,
      search: searchQuery.value,
      no_prices: showNoPrices.value.toString() // Ensure it's sent as string 'true' or 'false'
    };

    console.log('Fetching products with params:', params); // Debug log
    const response = await getProducts(params);
    console.log('API Response:', response); // Debug log

    products.value = response.results;
    totalItems.value = response.count;
    currentPage.value = response.current_page || page;

  } catch (err) {
    error.value = err.message || 'Error al cargar los productos';
    console.error('Error fetching products:', err);
  } finally {
    isLoading.value = false;
  }
};

// Modificar el método de búsqueda
const debounceSearch = (() => {
  let timeout;
  return () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      currentPage.value = 1; // Reset a primera página
      loadProducts(1);
    }, 300);
  };
})();

// Helper functions
const formatPrice = (price) => {
  return price.toLocaleString('es-CL');
};

const truncateText = (text, length) => {
  if (!text) return 'Sin descripción';
  return text.length > length ? `${text.slice(0, length)}...` : text;
};

const getMinPrice = (product) => {
  if (!product.blisters?.length) return 0;
  return Math.min(...product.blisters.map(b => parseFloat(b.price)));
};

const getMaxPrice = (product) => {
  if (!product.blisters?.length) return 0;
  return Math.max(...product.blisters.map(b => parseFloat(b.price)));
};

// Computed for products without prices
const productsWithoutPrices = computed(() => {
  return products.value.filter(product => 
    !product.price || product.price <= 0
  ).length;
});

// Ensure both display and filtering use the same logic
const filteredProducts = computed(() => {
  if (showNoPrices.value) {
    return products.value.filter(product => !product.price || product.price <= 0);
  }
  return products.value;
});

// Add toggle function
const toggleNoPrices = async () => {
  showNoPrices.value = !showNoPrices.value;
  currentPage.value = 1;
  await loadProducts(1);
};

const confirmDelete = (product) => {
  productToDelete.value = product;
  deleteModal.show();
};

const handleDelete = async () => {
  if (!productToDelete.value) return;
  
  try {
    isDeleting.value = true;
    const result = await deleteProduct(productToDelete.value.id);
    
    // Refresh products list immediately after successful deletion
    await loadProducts(
      // If we're on the last item of the page and it's not the first page,
      // go to previous page
      products.value.length === 1 && currentPage.value > 1 
        ? currentPage.value - 1 
        : currentPage.value
    );
    
    toast.success(result.message || 'Producto eliminado correctamente');
    deleteModal.hide();
  } catch (error) {
    console.error('Delete error:', error);
    toast.error(error.message || 'Error al eliminar el producto');
    
    // If product was already deleted, refresh list
    if (error.message?.includes('no existe')) {
      await loadProducts(currentPage.value);
    }
  } finally {
    isDeleting.value = false;
    productToDelete.value = null;
  }
};

const showBulkPriceUpdate = () => {
  bulkPriceUpdateModal.value?.show();
};

// Lifecycle
onMounted(() => {
  loadProducts(1);
  
  // Actualizar cada 5 minutos
  const refreshInterval = setInterval(() => {
    loadProducts(currentPage.value);
  }, 5 * 60 * 1000);

  // Limpiar el intervalo cuando el componente se destruye
  onUnmounted(() => {
    clearInterval(refreshInterval);
  });

  deleteModal = new Modal(document.getElementById('deleteProductModal'));
});

watch(searchQuery, () => {
  debounceSearch();
});

// Add watch for showNoPrices changes
watch(showNoPrices, () => {
  loadProducts(1);
});
</script>

<style scoped>
.card {
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease-in-out;
}

.card:hover {
  transform: translateY(-0.25rem);
}

.img-thumbnail {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 0.25rem;
}

.price {
  font-size: 0.9rem;
}

.badge {
  font-size: 0.75rem;
  white-space: nowrap;
}

.d-flex.align-items-center.gap-1 {
  flex-wrap: wrap;
}

/* Skeleton Loading */
.skeleton-loader {
  background: #1a1a1a;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.skeleton-text {
  height: 20px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Estilos para la paginación */
.pagination {
  --bs-pagination-color: var(--primary-color);
  --bs-pagination-hover-color: var(--secondary-color);
  --bs-pagination-focus-color: var(--secondary-color);
  --bs-pagination-active-bg: var(--primary-color);
  --bs-pagination-active-border-color: var(--primary-color);
}

.pagination .page-link {
  border: 1px solid var(--bs-gray-700);
  color: var(--black-color);
}

.pagination .page-item.active .page-link {
  z-index: 3;
  color: var(--black-color);
  background-color: var(--secondary-color);
}

.pagination .page-item.disabled .page-link {
  color: var(--bs-gray-600);
  pointer-events: none;
  background-color: var(--bs-dark);
  border-color: var(--bs-gray-700);
}

.btn-outline-light {
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.btn-outline-light:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.btn-outline-light.active {
  background-color: var(--bs-dark);
  border-color: var(--bs-danger);
  color: var(--bs-danger);
}

.badge {
  padding: 0.35em 0.65em;
  border-radius: 20px;
}

/* Automation Banner Styles */
.automation-banner {
  background: linear-gradient(45deg, #1a1a1a, #2a2a2a);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.automation-icon {
  background: rgba(var(--accent-color-rgb), 0.1);
  color: var(--accent-color);
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  animation: fa-spin 2s infinite linear;
}


.automation-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  background-color: #4CAF50;
  border-radius: 50%;
  display: inline-block;
  animation: pulse 2s infinite;
}

.status-text {
  color: #4CAF50;
  font-size: 0.875rem;
  font-weight: 500;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(76, 175, 80, 0);
  }
  
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

.btn-danger:hover {
  background-color: #bb2d3b;
  border-color: #b02a37;
  opacity: 1;
}
</style>